import { Building2, CalendarDays, Link2, MapPin, Stethoscope, User } from "lucide-react";
import { MessageBodyMarkdown } from "@/components/messages/message-body-markdown";
import { cn } from "@/lib/utils";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import { isDirectEntryEvent } from "@/lib/calendars";
import { formatTime, isCancelledCalendarStatus, isNewPatient } from "../calendar-utils";
import { formatLuxonDuration } from "./day-view-utils";

function EventDetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-[minmax(0,5.75rem)_1fr] gap-x-3 gap-y-0 text-xs leading-snug sm:grid-cols-[minmax(0,7rem)_1fr]">
      <dt className="shrink-0 pt-0.5 font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-sm text-foreground">{children}</dd>
    </div>
  );
}

export function SelectedDayEventDetails({ event, use12HourFormat }) {
  const cancelled = isCancelledCalendarStatus(event.status);
  const durationLabel = formatLuxonDuration(event.startAt, event.endAt);
  const timeRange = `${formatTime(event.startAt, { hour12: use12HourFormat })} – ${formatTime(event.endAt, { hour12: use12HourFormat })}`;
  const dateLine = event.startAt.toFormat("cccc, MMM d, yyyy");
  const tenantName = resolveTenantDisplayName(event._tenant);
  const locationNorm = String(event.location || "").trim().toLowerCase();
  const tenantNorm = tenantName.trim().toLowerCase();
  const showClinicRow = Boolean(tenantName) && tenantNorm !== locationNorm;

  const kindRaw = String(event.kind || "").trim().toLowerCase();
  const kindLabel =
    kindRaw === "slot"
      ? "Open slot"
      : kindRaw === "appointment"
        ? isDirectEntryEvent(event)
          ? "Calendar entry"
          : "Appointment"
        : kindRaw
          ? kindRaw
          : null;
  const calendarName = String(event.calendarName || "").trim();

  const statusLabel = String(event.status || "").trim();
  const newPatient = isNewPatient(event);
  const confirmationStatus = String(event.confirmationStatus || "").trim();

  return (
    <div className={cn("space-y-4", cancelled && "opacity-[0.92]")}>
      <div className="flex flex-wrap items-center gap-2">
        {kindLabel ? (
          <span className="inline-flex rounded-md border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {kindLabel}
          </span>
        ) : null}
        {newPatient ? (
          <span className="inline-flex rounded-md border border-violet-400/35 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800 dark:text-violet-200">
            New
          </span>
        ) : null}
        {statusLabel ? (
          <span
            className={cn(
              "inline-flex rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              cancelled
                ? "border-rose-400/35 bg-rose-500/10 text-rose-800 dark:text-rose-200"
                : "border-emerald-400/35 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100",
            )}
          >
            {statusLabel.replace(/_/g, " ")}
          </span>
        ) : null}
        {confirmationStatus === "confirmed" || confirmationStatus === "pending" ? (
          <span
            className={cn(
              "inline-flex rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              confirmationStatus === "confirmed"
                ? "border-emerald-400/35 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100"
                : "border-amber-400/35 bg-amber-500/10 text-amber-900 dark:text-amber-100",
            )}
          >
            {confirmationStatus === "confirmed" ? "Confirmed" : "Awaiting confirmation"}
          </span>
        ) : null}
      </div>

      <div>
        <p
          className={cn(
            "text-base font-semibold leading-snug text-foreground",
            cancelled && "line-through decoration-rose-500/55",
          )}
        >
          {event.title}
        </p>
      </div>

      <dl className="space-y-2.5 border-t border-border/60 pt-3">
        <EventDetailRow label="When">
          <span className={cn(cancelled && "line-through decoration-rose-500/40")}>
            <span className="block font-medium text-foreground">{dateLine}</span>
            <span className="mt-0.5 block tabular-nums text-muted-foreground">
              {timeRange}
              {durationLabel ? <span> · {durationLabel}</span> : null}
            </span>
          </span>
        </EventDetailRow>

        {event.timezone ? <EventDetailRow label="Timezone">{event.timezone}</EventDetailRow> : null}

        {event.patient && !newPatient && event.patient !== event.title ? (
          <EventDetailRow label="Patient">
            <span className="inline-flex items-start gap-2">
              <User className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span>{event.patient}</span>
            </span>
          </EventDetailRow>
        ) : null}

        {event.doctor ? (
          <EventDetailRow label="Doctor">
            <span className="inline-flex items-start gap-2">
              <Stethoscope className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span>{event.doctor}</span>
            </span>
          </EventDetailRow>
        ) : null}

        {event.location ? (
          <EventDetailRow label="Location">
            <span className="inline-flex items-start gap-2">
              <MapPin className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span>{event.location}</span>
            </span>
          </EventDetailRow>
        ) : null}

        {showClinicRow ? (
          <EventDetailRow label="Clinic">
            <span className="inline-flex items-start gap-2">
              <Building2 className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span>{tenantName}</span>
            </span>
          </EventDetailRow>
        ) : null}

        {calendarName ? (
          <EventDetailRow label="Calendar">
            <span className="inline-flex items-start gap-2">
              <CalendarDays className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span>{calendarName}</span>
            </span>
          </EventDetailRow>
        ) : null}

        {event.type ? <EventDetailRow label="Type">{event.type}</EventDetailRow> : null}

        {event.mode ? <EventDetailRow label="Mode">{event.mode}</EventDetailRow> : null}

        {/* {event.slotType ? <EventDetailRow label="Slot type">{event.slotType}</EventDetailRow> : null} */}

        {event.link ? (
          <EventDetailRow label="Link">
            <a
              href={event.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-start gap-1.5 wrap-break-word break-all text-primary underline-offset-2 hover:underline"
            >
              <Link2 className="mt-0.5 size-3.5 shrink-0 opacity-80" aria-hidden />
              <span>{event.link}</span>
            </a>
          </EventDetailRow>
        ) : null}

        {/* {event.episodeId ? <EventDetailRow label="Episode">{event.episodeId}</EventDetailRow> : null} */}

        {/* {event.calendarId ? <EventDetailRow label="Calendar ID">{event.calendarId}</EventDetailRow> : null} */}
      </dl>

      {cancelled && event.cancellationReason ? (
        <div className="rounded-lg border border-rose-400/30 bg-rose-500/5 p-3">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">
            Cancellation reason
          </p>
          <p className="text-sm text-foreground/90">{event.cancellationReason}</p>
        </div>
      ) : null}

      {event.description ? (
        <div className="rounded-lg border border-border/60 bg-muted/35 p-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Description</p>
          <MessageBodyMarkdown className="text-sm text-muted-foreground [&_a]:text-primary [&_a]:underline">
            {event.description}
          </MessageBodyMarkdown>
        </div>
      ) : null}
    </div>
  );
}
